// src/pages/HeathrowFareCompare.jsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import api from "../utils/api";
import "../styles/heathrowFareCompare.css";

const HeathrowFareCompare = () => {
  const MARKET_FLAGS = {
    heathrow_official: true,
    edisonlee: false,
    minicab: false,
    online_first: false,
    online_second: false,
    uber_reserve: false,
  };
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [datetime, setDatetime] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [luggage, setLuggage] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showMoreMarket, setShowMoreMarket] = useState(false);
  const autoSubmitted = React.useRef(false);

  // Prefill defaults
  useEffect(() => {
    const nextMondayAt11 = () => {
      const now = new Date();
      const day = now.getDay(); // 0=Sun
      const daysToMon = (8 - day) % 7 || 7;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(11, 0, 0, 0);
      const pad = n => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };
    setPickup("SL1 5JS");
    setDropoff("TW6 1QG");
    setPassengers(4);
    setLuggage(2);
    setDatetime(nextMondayAt11());
  }, []);

  const handleSwap = () => {
    setPickup(dropoff);
    setDropoff(pickup);
  };

  // Auto-submit once defaults are set
  useEffect(() => {
    if (autoSubmitted.current) return;
    if (pickup && dropoff && datetime) {
      autoSubmitted.current = true;
      handleSubmit(new Event("submit"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, dropoff, datetime]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setHasSubmitted(true);
    setResult(null);

    if (!pickup.trim() || !dropoff.trim()) {
      setError("Please enter both pickup and drop-off postcodes (or Heathrow).");
      return;
    }
    if (!datetime) {
      setError("Please choose a pickup date and time.");
      return;
    }

    const payload = {
      pickup_postcode: pickup.trim(),
      dropoff_postcode: dropoff.trim(),
      pickup_datetime: datetime,
      passengers: Number(passengers),
      luggage: Number(luggage)
    };

    try {
      setLoading(true);
      const res = await api.post("/api/fare-compare", payload);
      setResult(res.data);
    } catch (err) {
      console.error("❌ Fare compare error:", err);
      const msg =
        err?.response?.data?.details ||
        err?.response?.data?.error ||
        "We could not generate a fare comparison for this route.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderMoney = value =>
    typeof value === "number" ? `£${value.toFixed(2)}` : "—";

  const bandAbsOrPct = (mid, { abs = 1, pct = 0.08, threshold = 120 } = {}) => {
    if (mid == null || Number.isNaN(Number(mid))) return null;
    const m = Number(mid);
    if (m <= threshold) {
      return {
        min: Number((m - abs).toFixed(2)),
        mid: Number(m.toFixed(2)),
        max: Number((m + abs).toFixed(2)),
      };
    }
    return {
      min: Number((m * (1 - pct)).toFixed(2)),
      mid: Number(m.toFixed(2)),
      max: Number((m * (1 + pct)).toFixed(2)),
    };
  };

  const marketExtras = () => {
    const m = result?.fares?.market;
    if (!m) return [];
    const entries = [
      { key: "heathrow_official", label: "Heathrow official (saloon)", val: m.heathrow_official?.saloon },
      { key: "edisonlee", label: "Edison Lee (saloon)", val: m.edisonlee?.saloon },
      { key: "minicab", label: "Minicab (saloon)", val: m.minicab?.saloon },
      { key: "online_first", label: "Online taxi 1st (saloon)", val: m.online_taxi_first?.mpv },
      { key: "online_second", label: "Online taxi 2nd (saloon)", val: m.online_taxi_second?.mpv },
      { key: "uber_reserve", label: "Uber Reserve (saloon)", val: m.uber_reserve?.saloon },
    ];
    return entries
      .filter(e => MARKET_FLAGS[e.key])
      .map(e => ({ ...e, range: bandAbsOrPct(e.val) }))
      .filter(e => e.range);
  };

  const renderBlackCabRange = fares => {
    const bc = fares?.black_cab;
    if (!bc) return null;

    return (
      <p style={{ marginTop: 4, fontSize: 14 }}>
        <strong>Estimated range:</strong>{" "}
        <span className="provider-price">{renderMoney(bc.min)}</span> –{" "}
        <span className="provider-price">{renderMoney(bc.max)}</span>{" "}
        {bc.mid && (
          <span>
            (typical ~<span className="provider-price">{renderMoney(bc.mid)}</span>)
          </span>
        )}
      </p>
    );
  };

  const renderRideHailRange = fares => {
    const rh = fares?.ride_hail;
    if (!rh) return null;
    return (
      <p style={{ marginTop: 4, fontSize: 14 }}>
        <strong>Estimated range:</strong>{" "}
        <span className="provider-price">{renderMoney(rh.min)}</span> –{" "}
        <span className="provider-price">{renderMoney(rh.max)}</span>
      </p>
    );
  };

  const journeyDistanceText =
    result?.journey &&
    `Distance: ~${result.journey.distance_miles} miles (${result.journey.distance_km} km)`;

  const confidence = result?.summary?.confidence || "Low";
  const notes = result?.summary?.notes || [];
  const benchmarksCapturedAt = result?.fares?.benchmarks?.captured_at
    ? new Date(result.fares.benchmarks.captured_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
    : "—";
  const ccgVsBlackCabMid = result?.summary?.ccg_vs_blackcab_mid ?? null;
  const showBestValue = Boolean(result?.fares?.cheapcabgrab);
  const bestValueLine =
    ccgVsBlackCabMid != null && Math.abs(ccgVsBlackCabMid) < 5
      ? "🟡 Prices are similar — fixed fares avoid surge & meter risk"
      : "🟢 Best value on this route: CheapCabGrab (fixed fare)";

  return (
    <div className="tool-page">
      <Helmet>
        <title>
          Heathrow Taxi Fare Comparison | London Taxi vs Uber vs CheapCabGrab
        </title>

        <meta
          name="description"
          content="Compare estimated Heathrow taxi fares between London black cabs, ride-hailing apps and CheapCabGrab’s fixed prices. Enter your postcode and see typical one-way costs."
        />
        <meta
          name="keywords"
          content="Heathrow taxi fare comparison, Heathrow taxi prices, London taxi vs Uber Heathrow, Heathrow cab cost, Heathrow taxi estimate, airport transfers"
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/heathrow-fare-compare"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Heathrow Taxi Fare Comparison | CheapCabGrab"
        />
        <meta
          property="og:description"
          content="See how CheapCabGrab compares against London taxis and ride-hailing apps for Heathrow journeys."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/heathrow-fare-compare"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                name: "Heathrow Taxi Fare Comparison",
                applicationCategory: "TravelApplication",
                operatingSystem: "Web",
                url: "https://www.cheapcabgrab.com/heathrow-fare-compare",
                offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" }
              },
              {
                "@type": "WebPage",
                name: "Heathrow Taxi Fare Comparison",
                url: "https://www.cheapcabgrab.com/heathrow-fare-compare",
                description:
                  "Compare estimated Heathrow taxi fares between London taxis, ride-hailing apps and CheapCabGrab fixed prices."
              },
              {
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "Is Uber always cheaper for Heathrow?",
                    acceptedAnswer: { "@type": "Answer", text: "No. Surge pricing, luggage limits, and availability can make fixed fares more predictable for Heathrow trips." }
                  },
                  {
                    "@type": "Question",
                    name: "Can I pre-book Uber everywhere?",
                    acceptedAnswer: { "@type": "Answer", text: "Not in all areas; availability varies, especially for groups or larger vehicles." }
                  },
                  {
                    "@type": "Question",
                    name: "What if I have 4 passengers and 3–4 bags?",
                    acceptedAnswer: { "@type": "Answer", text: "Ride-hail availability can be limited at peak times; pre-booking a fixed fare is usually safer." }
                  }
                ]
              }
            ]
          })}
        </script>

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Taxi Fare Comparison",
            url: "https://www.cheapcabgrab.com/heathrow-fare-compare",
            description:
              "Compare estimated Heathrow taxi fares between London taxis, ride-hailing apps and CheapCabGrab fixed prices.",
            about: [
              {
                "@type": "Service",
                name: "Heathrow Taxi",
                url: "https://www.cheapcabgrab.com/heathrow-taxi"
              },
              {
                "@type": "Service",
                name: "Airport Transfers",
                url: "https://www.cheapcabgrab.com"
              }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <main className="tool-container">
        {/* HEADER */}
        <header className="tool-header">
          <h1>Heathrow Taxi Fare Comparison</h1>
          <p className="tool-intro">
            Compare estimated one-way fares between{" "}
            <strong>CheapCabGrab</strong>,{" "}
            <strong>London taxis</strong>,{" "}
            <strong>ride-hailing apps</strong> for journeys where
            one end is Heathrow.
            <br />
            Enter your postcode and see how prices typically compare.
          </p>
          <p style={{ fontSize: 12, color: "#475467" }}>Last Updated: 14 Jan 2026</p>
        </header>

        {/* FORM + RESULTS GRID */}
        <section className="tool-grid">
          {/* FORM CARD */}
          <article className="tool-card">
            <h2>Enter Your Journey</h2>
            <p style={{ fontSize: 14, marginBottom: 10 }}>
              One end must be <strong>Heathrow</strong> (e.g.{" "}
              <em>Heathrow Airport</em> or <em>TW6 1QG</em>).
            </p>

            <form className="tool-form" onSubmit={handleSubmit}>
              <div className="tool-form-row">
                <label htmlFor="pickup">
                  Pickup postcode or Heathrow
                </label>
                <input
                  id="pickup"
                  type="text"
                  value={pickup}
                  onChange={e => setPickup(e.target.value)}
                  placeholder="e.g. SL1 1XY or Heathrow Airport"
                  autoComplete="off"
                />
              </div>

              <button
                type="button"
                className="tool-swap-btn"
                onClick={handleSwap}
              >
                ⇅ Swap pickup &amp; drop-off
              </button>

              <div className="tool-form-row">
                <label htmlFor="dropoff">
                  Drop-off postcode or Heathrow
                </label>
                <input
                  id="dropoff"
                  type="text"
                  value={dropoff}
                  onChange={e => setDropoff(e.target.value)}
                  placeholder="e.g. TW6 1QG or Heathrow Terminal 3"
                  autoComplete="off"
                />
              </div>

              <div className="tool-form-row">
                <label htmlFor="datetime">Pickup date &amp; time</label>
                <input
                  id="datetime"
                  type="datetime-local"
                  value={datetime}
                  onChange={e => setDatetime(e.target.value)}
                />
              </div>

              <div className="tool-form-row tool-form-row-inline">
                <div>
                 <label htmlFor="passengers">Passengers</label>
                  <input
                    id="passengers"
                    type="number"
                    min="1"
                    max="4"
                    step="1"
                    value={passengers}
                    onChange={e =>
                      setPassengers(
                        e.target.value === ""
                          ? ""
                          : Math.min(4, Math.max(1, Number(e.target.value)))
                      )
                    }
                    onBlur={() => {
                      const num = Number(passengers);
                      if (Number.isNaN(num)) setPassengers(1);
                      else setPassengers(Math.min(4, Math.max(1, num)));
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="luggage">Large suitcases</label>
                  <input
                    id="luggage"
                    type="number"
                    min="0"
                    max="3"
                    step="1"
                    value={luggage}
                    onChange={e =>
                      setLuggage(
                        e.target.value === ""
                          ? ""
                          : Math.min(3, Math.max(0, Number(e.target.value)))
                      )
                    }
                    onBlur={() => {
                      const num = Number(luggage);
                      if (Number.isNaN(num)) setLuggage(0);
                      else setLuggage(Math.min(3, Math.max(0, num)));
                    }}
                  />
                </div>
              </div>

              {error && (
                <div className="tool-error" style={{ marginTop: 8 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="primary-btn"
                disabled={loading}
                style={{ marginTop: 10 }}
              >
                {loading ? "Comparing fares..." : "Compare Heathrow Taxi Fares"}
              </button>

              <p
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: "#555",
                  lineHeight: 1.5
                }}
              >
                This tool is for <strong>Heathrow journeys only</strong>. For
                standard taxi bookings, use the{" "}
                <Link to="/">Instant Fare Quote</Link> on the home page.
              </p>
            </form>
          </article>

          {/* RESULTS CARD */}
          <article className="tool-card tool-results-card">
            <h2>Fare Comparison Results</h2>

            {!hasSubmitted && (
              <p style={{ fontSize: 14 }}>
                Enter your pickup, drop-off and date/time to see how fares
                typically compare between providers for this Heathrow journey.
              </p>
            )}

            {hasSubmitted && !loading && !result && !error && (
              <p style={{ fontSize: 14 }}>
                No results yet. Please try again with a different route.
              </p>
            )}

            {result && (
              <>
                {/* Journey summary */}
                <div className="tool-summary">
                  <h3>Journey Summary</h3>
                  <p style={{ fontSize: 14 }}>
                    <strong>From:</strong> {result.journey.pickup_postcode}
                    <br />
                    <strong>To:</strong> {result.journey.dropoff_postcode}
                    <br />
                    <strong>Pickup:</strong>{" "}
                    {new Date(
                      result.journey.pickup_datetime
                    ).toLocaleString("en-GB", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false
                    })}
                    <br />
                    <strong>Passengers:</strong>{" "}
                    {result.journey.passengers} ·{" "}
                    <strong>Luggage:</strong> {result.journey.luggage} large
                    bags
                    <br />
                    {journeyDistanceText && (
                      <span>
                        <strong>{journeyDistanceText}</strong>
                      </span>
                    )}
                    <br />
                    <strong>Drive time:</strong>{" "}
                    {result.journey.duration_min != null
                      ? `${result.journey.duration_min} min`
                      : "—"}
                    <br />
                    <strong>Distance source:</strong>{" "}
                    {result.journey.distance_source || "—"}
                  </p>
                  <div style={{ marginTop: 8, fontSize: 13, color: "#555" }}>
                    <strong>Confidence:</strong> {confidence}
                    {notes.length > 0 && (
                      <ul
                        style={{
                          marginTop: 6,
                          marginBottom: 0,
                          paddingLeft: 18
                        }}
                      >
                        {notes.map((n, idx) => (
                          <li key={idx}>{n}</li>
                        ))}
                      </ul>
                    )}
                    {showBestValue && (
                      <p className="best-value-line">
                        {bestValueLine}
                      </p>
                    )}
                    <p style={{ marginTop: 6 }}>
                      If peace of mind matters more than saving a few pounds, fixed fares are usually the safest option for Heathrow journeys.
                    </p>
                    <p style={{ marginTop: 6 }}>
                      {`With ${result.journey.passengers} passenger${result.journey.passengers > 1 ? "s" : ""} and ${result.journey.luggage} large bag${result.journey.luggage === 1 ? "" : "s"}, ride-hailing availability can be limited at peak times.`}
                    </p>
                  </div>
                </div>

                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      borderRadius: 10,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    color: "#9a3412",
                    fontSize: 13
                  }}
                >
                  🚨 Last-minute booking warning (airport trips): Ride-hailing apps can show lower prices early, but availability and surge pricing often change close to departure — especially with luggage or groups.
                </div>

                {/* Providers grid */}
                <div className="tool-providers-grid">
                  {/* CheapCabGrab */}
                  {result.fares?.cheapcabgrab && (
                    <div className="tool-provider-card highlight" style={{ borderColor: "#0ea5e9", background: "#e0f2fe" }}>
                      <h3>CheapCabGrab (Fixed Fare)</h3>
                      <p style={{ fontSize: 14 }}>
                        <strong>Estimated one-way fare:</strong>{" "}
                        <span className="provider-price ccg-price">
                          {renderMoney(
                            result.fares.cheapcabgrab.one_way
                          )}
                        </span>
                      </p>
                      {result.fares.cheapcabgrab.vehicle && (
                        <p style={{ fontSize: 13, marginTop: 2 }}>
                          Vehicle:{" "}
                          {result.fares.cheapcabgrab.vehicle
                            .charAt(0)
                            .toUpperCase() +
                            result.fares.cheapcabgrab.vehicle.slice(1)}
                        </p>
                      )}
                      <p style={{ fontSize: 13, marginTop: 6 }}>
                        <strong>Key points:</strong> fixed price, pre-booked,
                        licensed private hire driver. No meter, price agreed
                        before travel.
                      </p>
                      <p style={{ fontSize: 13, marginTop: 4 }}>
                        Once booked, your price does not change — even if traffic or flight times shift.
                      </p>
                      {typeof result.summary?.ccg_vs_blackcab_mid === "number" && (
                        <p style={{ fontSize: 13, marginTop: 6, color: "#0a7" }}>
                          CheapCabGrab is around £{result.summary.ccg_vs_blackcab_mid.toFixed(2)}{" "}
                          cheaper than a typical black cab estimate on this route.
                        </p>
                      )}
                      <p className="ccg-reassure">
                        ✔ Licensed drivers · ✔ Fixed price · ✔ No surge · ✔ Meet &amp; greet available
                      </p>
                      <p style={{ fontSize: 13, marginTop: 8 }}>
                        ✅ Lock this fixed price now — get an{" "}
                        <Link to="/">instant quote</Link> (no obligation)
                      </p>
                      <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.6 }}>
                        <strong>Stress level:</strong>
                        <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 18 }}>
                          <li>✅ Price locked · vehicle guaranteed · pre-booked driver</li>
                          <li>⚠️ Slightly higher than off-peak app fares</li>
                        </ul>
                        <div>Hassle level: 🟢 Low · Price risk: 🟢 Low</div>
                      </div>
                    </div>
                  )}

                  {/* Ride-hailing */}
                  {result.fares?.ride_hail && (
                    <div className="tool-provider-card" style={{ borderColor: "#e5e7eb", background: "#f8fafc" }}>
                      <h3>Ride-hailing Apps (Uber/Bolt etc.)</h3>
                      {renderRideHailRange(result.fares)}
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        Often looks cheaper early; not always cheaper at pickup. Advance “Reserve” can cost more and isn’t available in all areas.
                      </p>
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        App pricing can surge and may include airport fees depending on provider.
                      </p>
                      <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.6 }}>
                        <strong>Stress level:</strong>
                        <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 18 }}>
                          <li>⚠️ Price may surge; airport pickup delays possible</li>
                          <li>⚠️ Vehicle not guaranteed; advance booking not available everywhere</li>
                          <li>⚠️ Larger groups/bags can be refused last minute</li>
                        </ul>
                        <div>Hassle level: 🟠 Medium · Price risk: 🔴 High at peak</div>
                      </div>
                    </div>
                  )}

                  {/* Black cab */}
                  {result.fares?.black_cab && (
                    <div className="tool-provider-card" style={{ borderColor: "#e5e7eb", background: "#f8fafc" }}>
                      <h3>London Taxi / Black Cab (Metered)</h3>
                      {renderBlackCabRange(result.fares)}
                      {result.fares.black_cab_note && (
                        <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                          {result.fares.black_cab_note}
                        </p>
                      )}
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        Metered fares vary with traffic and waiting time.
                      </p>
                      <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.6 }}>
                        <strong>Stress level:</strong>
                        <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 18 }}>
                          <li>✅ Licensed + bus lane access</li>
                          <li>⚠️ Final price depends on meter/time in traffic</li>
                        </ul>
                        <div>Hassle level: 🟠 Medium · Price risk: 🟠 Meter-based</div>
                      </div>
                    </div>
                  )}

                  {/* Airport taxi benchmark */}
                  {result.fares?.benchmarks?.airport_taxi?.saloon && (
                    <div className="tool-provider-card">
                      <h3>Airport Taxi (Benchmark)</h3>
                      <p style={{ marginTop: 4, fontSize: 14 }}>
                        <strong>Typical one-way (saloon):</strong>{" "}
                        <span className="provider-price benchmark-price">
                          {renderMoney(result.fares.benchmarks.airport_taxi.saloon.min)}
                        </span>{" "}
                        –{" "}
                        <span className="provider-price benchmark-price">
                          {renderMoney(result.fares.benchmarks.airport_taxi.saloon.max)}
                        </span>
                      </p>
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        Reference quote ({result.fares.benchmarks.match}) — last updated{" "}
                        {benchmarksCapturedAt}
                      </p>
                    </div>
                  )}

                  {/* Online taxi benchmark (collapsible) */}
                  {result.fares?.benchmarks?.online_taxi?.saloon && (
                    <>
                      <div className="market-toggle">
                        <button type="button" onClick={() => setShowMoreMarket(s => !s)}>
                          {showMoreMarket ? "Hide more market prices ▲" : "Show more market prices ▾"}
                        </button>
                      </div>
                      {showMoreMarket && (
                        <div className="tool-provider-card">
                          <h3>Online Taxi (Benchmark)</h3>
                          <p style={{ marginTop: 4, fontSize: 14 }}>
                            <strong>Typical one-way (saloon):</strong>{" "}
                            <span className="provider-price benchmark-price">
                              {renderMoney(result.fares.benchmarks.online_taxi.saloon.min)}
                            </span>{" "}
                            –{" "}
                            <span className="provider-price benchmark-price">
                              {renderMoney(result.fares.benchmarks.online_taxi.saloon.max)}
                            </span>
                          </p>
                          <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                            Reference quote ({result.fares.benchmarks.match}) — last updated{" "}
                            {benchmarksCapturedAt}
                          </p>
                        </div>
                      )}
                      {showMoreMarket && marketExtras().length > 0 && (
                        <div className="tool-provider-card">
                          <h3>More market prices (beta)</h3>
                          {marketExtras().map((e, idx) => (
                            <p key={idx} style={{ marginTop: idx ? 8 : 4, fontSize: 14 }}>
                              <strong>{e.label}:</strong>{" "}
                              <span className="provider-price benchmark-price">
                                {renderMoney(e.range.min)}
                              </span>{" "}
                              –{" "}
                              <span className="provider-price benchmark-price">
                                {renderMoney(e.range.max)}
                              </span>
                            </p>
                          ))}
                          {result.fares.market_match && (
                            <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                              Source: DB match {result.fares.market_match}
                              {result.fares.market?.captured_at
                                ? ` • captured ${new Date(result.fares.market.captured_at).toLocaleString("en-GB")}`
                                : ""}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    fontSize: 13
                  }}
                >
                  <strong>Quick takeaway (copy/paste):</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    <li>Uber looks cheaper early, not always cheaper at pickup.</li>
                    <li>Fixed fares cost more upfront, less stress later.</li>
                    <li>Groups + luggage benefit most from pre-booking.</li>
                  </ul>
                  </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    fontSize: 13
                  }}
                >
                  <strong>Why this calculator is useful:</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    <li>Shows fixed fare vs metered vs app prices side by side.</li>
                    <li>Highlights surge/availability risk for groups and luggage.</li>
                    <li>Links to official sources so you can verify fares yourself.</li>
                  </ul>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    fontSize: 13
                  }}
                >
                  <strong>Official sources:</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    <li><a href="https://tfl.gov.uk/" target="_blank" rel="noreferrer">TfL fares &amp; travel info</a></li>
                    <li><a href="https://www.heathrow.com/transport-and-directions" target="_blank" rel="noreferrer">Heathrow transport &amp; charges</a></li>
                  </ul>
                </div>

                {/* Disclaimer */}
                <div className="tool-disclaimer">
                  <p style={{ fontSize: 12, color: "#555" }}>
                    {result.disclaimer ||
                      "These fare ranges are estimates only and not live prices. Always confirm the exact fare in the taxi, app or booking system before travel."}
                  </p>
                </div>

                {/* Cross-links */}
                <div style={{ marginTop: 16 }}>
                  <strong>Plan your Heathrow journey:</strong>
                  <ul style={{ marginTop: 6, fontSize: 14, lineHeight: 1.6 }}>
                    <li>🚖 <Link to="/heathrow-fare-compare">Compare taxi fares</Link></li>
                    <li>🚆 <Link to="/train-vs-taxi">Train vs taxi</Link></li>
                    <li>🅿️ <Link to="/heathrow-parking-vs-taxi">Parking vs taxi</Link></li>
                    <li>✈️ <Link to="/flight-checker">Check your flight</Link></li>
                    <li>🕒 <Link to="/leave-for-heathrow">When to leave for Heathrow</Link></li>
                    <li>🧭 <Link to="/terminalFinder">Find your terminal</Link></li>
                    <li>🧳 <Link to="/arrival-assistant">Arrival assistant</Link></li>
                  </ul>
                </div>
              </>
            )}
          </article>
        </section>

        {/* REVIEWS AT BOTTOM (consistent with other tools) */}
        <section className="tool-section tool-explainer" style={{ marginTop: 24 }}>
          <h2>How Heathrow taxi fares are calculated</h2>
          <p>
            Taxi and airport transfer prices vary by distance, time of day, traffic, and each provider’s pricing model. For Heathrow trips, some operators use fixed prices agreed in advance, while others rely on meters or dynamic app-based pricing. We base our estimates on real road distance and typical journey times (not crow-fly distance), using routing data from services like OSRM to estimate how long a journey normally takes.
          </p>
          <p>
            Metered London taxis (black cabs) typically charge a flag-fall fee plus per-mile rates and time-based charges when stationary or moving slowly, so final fares rise during congestion. Ride-hailing apps can add surge pricing at busy times, and airport fees vary by provider. To add context, we compare results against recent market benchmarks from airport taxi operators and online booking platforms. These references are not live quotes, but they help show what travellers typically pay for similar Heathrow routes.
          </p>
          <h3>Why fixed fares can differ from metered taxis</h3>
          <p>
            Fixed-price airport transfers are calculated in advance based on expected distance, time, and vehicle type. Once booked, the price does not change because of traffic, delays, or route adjustments, making them predictable when timing matters. Metered and app-based services can look cheaper at first, but the final cost may increase due to congestion, waiting time, or high demand. Prices shown here are estimates to help you compare typical costs rather than guarantee a final fare.
          </p>
          <p style={{ marginBottom: 0 }}>
            Our tools continuously review routing data, traffic conditions, and historical pricing patterns to provide realistic comparisons. For full control over travel time, passengers, and luggage, use the full fare comparison tool for a more detailed breakdown.
          </p>
        </section>

        <section className="tool-section" style={{ marginTop: 24 }}>
          <h2>FAQs</h2>
          <ul style={{ lineHeight: 1.6, paddingLeft: 18 }}>
            <li><strong>Is Uber always cheaper for Heathrow?</strong> No. Surge pricing, luggage limits, and availability can make fixed fares more predictable.</li>
            <li><strong>Can I pre-book Uber everywhere?</strong> Not in all areas; availability varies, especially for groups and larger vehicles.</li>
            <li><strong>What if I have 4 passengers and 3–4 bags?</strong> Ride-hail availability can be limited; pre-booking a fixed fare is usually safer.</li>
            <li><strong>Where can I verify official fares?</strong> Check <a href="https://tfl.gov.uk/" target="_blank" rel="noreferrer">TfL</a> and <a href="https://www.heathrow.com/transport-and-directions" target="_blank" rel="noreferrer">Heathrow transport</a> for official guidance.</li>
          </ul>
        </section>

        <section className="tool-section" style={{ marginTop: 32 }}>
          <h2>Customer Reviews</h2>
          <ReviewsBar />
        </section>
      </main>
    </div>
  );
};

export default HeathrowFareCompare;
